import React from "react";
import { Formik, Form, Field } from "formik";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { Button, Field as UiField, Input } from "../ui";
import StarsIcon from "../../styles/StarsIcon";
import { AuthCover, AuthHeading } from "./AuthCover";
import { forgotPassword } from "../../shared/_services/auth.services";
import { setChallengeToken } from "../../shared/_helpers/otpChallenge";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const handleAuthentication = async ({ email }) => {
    let updatedemail = email.toLowerCase();
    const data = await forgotPassword({ email: updatedemail });
    if (data.status === 200) {
      // Backend SEC-114: /generate-otp returns the challenge token that
      // /otp-verify requires to redeem the code it just sent. Without it the
      // verify screen has no credential and every correct code gets a 401.
      // The purpose rides with the credential rather than with the
      // `verifyCodeFrom` breadcrumb: this is the only flow allowed to mint a
      // password-reset token, and a breadcrumb outlives the flow that set it.
      setChallengeToken(data?.data?.challengeToken, { purpose: 'reset_password' });
      window.localStorage.setItem('resetPasswordEmail', email);
      enqueueSnackbar(data?.data.message, {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });

      sessionStorage.setItem('verifyCodeFrom', 'forgotpassword');
      navigate('/verify-code', { state: { from: 'forgotpassword' } });
    }
    else {
      enqueueSnackbar(data?.response?.data?.error || data?.response?.data?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
  };

  const initialValues = {
    email: "",
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required")
      .test(
        "contains-at-and-dot",
        "Email must contain '@' and '.'",
        (value) => {
          if (!value) return false; // Required validation
          return value.includes("@") && value.includes(".");
        }
      )
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await handleAuthentication(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCover
      logoTo="/"
      art={
        <div className="tw-grid tw-gap-4">
          <h2 className="tw-m-0 tw-max-w-sm tw-text-3xl tw-font-semibold tw-text-text">
            Forgot Your Password?
          </h2>
          <p className="tw-m-0 tw-max-w-md tw-text-text">
            No worries. Enter your registered phone number and we’ll send you a verification code to reset your password and regain access to your financial dashboard.
          </p>
          <p className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-widest tw-text-text max-rail:tw-hidden">
            PROFESSIONAL FINANCIAL ADVISORY</p>
        </div>
      }
    >
      <AuthHeading title="Forgot Password" />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form>
            <UiField label="Email address" error={touched.email && errors.email}>
              <Field
                as={Input}
                type="email"
                id="email"
                name="email"
                placeholder="Email address"
              />
            </UiField>
            <p className="tw-text-sm tw-font-medium tw-text-text">
              Enter your email and we’ll send you link to reset your password.
            </p>
            <Button type="submit" className="tw-mb-2 tw-mt-4 tw-w-full" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send Verification Code"}
            </Button>
            <p className="tw-mt-3 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-sm">
              <StarsIcon name="chevronLeft" className="tw-h-4 tw-w-4 tw-shrink-0" />
              <Link className="tw-font-bold tw-text-link" to="/login">
                Back to login
              </Link>
            </p>
          </Form>
        )}
      </Formik>
    </AuthCover>
  );
};

export default ForgotPassword;
